/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Input,
	Output,
	EventEmitter,
	Component,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ViewChild,
	ElementRef,
	Renderer2,
	forwardRef,
} from '@angular/core';

import {
	UntypedFormBuilder,
	Validators,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	AbstractControl,
	ValidatorFn,
} from '@angular/forms';

import { Subject, Observable, EMPTY } from 'rxjs';

import * as strings from '@touchplan/strings';

import { Variance } from '@touchplan/common';
import { ShakeAnimation } from 'src/app/app-data/animations/shake';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';

export interface VarianceFormData {
	varianceId: string;
	varianceComment: string;
}

export function varianceFormDataValidator(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		if (control && control.value && control.value['varianceId']) {
			return null;
		}
		return { varianceFormData: { value: control.value } };
	};
}

/**
 * Component is set up to work in one of two modes, as a form component or with standard input and output.
 * As it stands only the "ui -> data" side of the ControlValueAccessor api is implmented.
 * This means that the component will always start empty and will not respond to
 * external data updates since there doesn't seem to be any need.
 *
 * ## both cases
 * - variance$ is the observable that outputs the live list of project variances
 * - done is emitted on clicking save with the [[VarianceFormData]]
 * - cancel is emitted on clicking cancel
 *
 * ## form api
 * - use as formControl
 * - inputs and output all work
 */
@Component({
	selector: 'variance-prompt',
	templateUrl: './variance-prompt.component.html',
	styles: [],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VariancePromptComponent),
			multi: true,
		},
		LoggerServiceFactory.provide('VariancePromptComponent'),
	],
})
export class VariancePromptComponent implements OnInit, OnDestroy, ControlValueAccessor {
	public strings = strings;
	private shakeAnimation: ShakeAnimation;

	@Input() public variance$: Observable<Map<string, Variance>>;
	@Input() public shake$: Observable<any>;

	@Output() public done = new EventEmitter<VarianceFormData>();
	@Output() public canceled = new EventEmitter<boolean>();

	//ui configuration - defaults here
	@Input() public showCancelButton = true;
	@Input() public saveButtonText = 'Save';

	@ViewChild('animationElement', { read: ElementRef, static: true }) public animationElement: ElementRef;
	private shutdownSubject = new Subject<void>();

	public test = false;

	public editForm = this.formBuilder.group({
		varianceId: ['', Validators.required],
		varianceComment: [''],
	});

	constructor(
		private formBuilder: UntypedFormBuilder,
		private renderer: Renderer2,
		private logger: DefaultLoggerService
	) {}

	public ngOnInit() {
		if (!this.shake$) {
			this.shake$ = EMPTY;
		}

		this.shakeAnimation = new ShakeAnimation(this.renderer, this.animationElement);
		this.shakeAnimation.observe(this.shake$);

		this.editForm.valueChanges.subscribe((change) => {
			this.onChange(change);
		});
	}

	public toggleTest() {
		this.test = !this.test;
	}
	public save() {
		if (!this.editForm.valid) {
			// this.internalShake.next();
			this.shakeAnimation.pulse();
			return;
		}
		this.done.emit(this.editForm.value);
	}
	public cancel() {
		this.canceled.emit();
	}

	public ngOnDestroy() {
		this.shutdownSubject.next();
		this.shutdownSubject.complete();
		this.shakeAnimation.destroy();
	}

	//form api
	// Both onChange and onTouched are functions
	public onChange: any = () => {};
	public onTouched: any = () => {};

	//write from data updates
	public writeValue(obj: any) {
		this.logger.log('--------variance prompt write value', obj);
		if (!obj || typeof obj !== 'object') {
			obj = { varianceId: null, varianceComment: null };
		}
		this.editForm.setValue(obj, { emitEvent: false });
	}
	/**
	 * Registers a callback function that should be called when
	 * the control's value changes in the UI.
	 */
	public registerOnChange(fn: any) {
		this.onChange = fn;
	}
	/**
	 * Registers a callback function that should be called when
	 * the control receives a blur event.
	 */
	public registerOnTouched(fn: any) {
		this.onTouched = fn;
	}
	/**
	 * This function is called by the forms API when
	 * the control status changes to or from "DISABLED".
	 */
	// public setDisabledState(isDisabled: boolean){
	// 	this.logger.log('setDisabledState, just in case it breaks things', isDisabled, this);
	// 	if(isDisabled){ this.internalField.disable({emitEvent: false}); }
	// 	else{ this.internalField.enable({emitEvent: false}); }
	// }
}
